import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { IMG } from "@/lib/content";
import { MaskedLines, EASE } from "@/components/motion/Reveal";
import { Button } from "@/components/motion/Button";
import { scrollTo } from "@/hooks/useLenis";
import { useIntro } from "@/components/Intro";

export function Hero() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { done } = useIntro();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [1.2, -1.2]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-1.2, 1.2]), { stiffness: 60, damping: 20 });
  const onMove = (e) => {
    if (reduced) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-ink text-paper" style={{ perspective: 1400 }} aria-label="Intro">
      <motion.div className="absolute inset-0" style={reduced ? undefined : { y: imgY, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}>
        <motion.img
          src={IMG.hero}
          alt="Helles, modernes Wohninterieur mit Naturmaterialien und Tageslicht"
          fetchPriority="high"
          sizes="100vw"
          className="w-full h-full object-cover scale-[1.08]"
          initial={reduced ? false : { scale: 1.22, filter: "brightness(0.6)" }}
          animate={done ? { scale: 1.08, filter: "brightness(0.9)" } : undefined}
          transition={{ duration: 2.4, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/15 to-ink/80" />

      <motion.div style={reduced ? undefined : { y: textY, opacity: fade }} className="relative z-10 h-full container-x flex flex-col justify-end pb-24 md:pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={done ? { opacity: 1 } : undefined}
          transition={{ duration: 1, delay: 0.6 }}
          className="label text-paper/70 mb-8 flex items-center gap-4"
        >
          <span className="w-8 h-px bg-paper/50" /> Emini Reinigung · Deutschschweiz
        </motion.p>
        <MaskedLines
          as="h1"
          data-testid="hero-title"
          inView={done}
          delay={0.35}
          lines={["Sauber.", "Bis ins Detail."]}
          className="display text-[13vw] sm:text-[11vw] lg:text-[8.6vw] xl:text-[7.8rem] text-paper"
        />
        <div className="mt-10 md:mt-12 grid md:grid-cols-12 gap-8 items-end">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={done ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 1, ease: EASE, delay: 0.9 }}
            className="md:col-span-5 lg:col-span-4"
          >
            <p className="text-base md:text-lg text-paper/85 leading-relaxed max-w-md">
              Professionelle Reinigung für Privat- und Geschäftsliegenschaften.
            </p>
            <p className="label text-paper/55 mt-5 leading-[1.9]">
              Schweizer Qualität.<br />Zuverlässige Ausführung.<br />Klare Kommunikation.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={done ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 1, ease: EASE, delay: 1.05 }}
            className="md:col-span-7 lg:col-span-8 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <Button to="/kontakt" variant="light" data-testid="hero-cta-offerte">Offertanfrage</Button>
            <Button variant="outlineLight" onClick={() => scrollTo("#leistungen")} data-testid="hero-cta-leistungen">Leistungen</Button>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={done ? { opacity: 1 } : undefined}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-3"
        aria-hidden
      >
        <span className="label text-paper/40 text-[9px]">Scroll</span>
        <span className="relative w-px h-12 overflow-hidden bg-paper/20">
          <motion.span className="absolute inset-x-0 top-0 h-1/2 bg-paper" animate={{ y: ["-100%", "200%"] }} transition={{ duration: 2.2, ease: "easeInOut", repeat: Infinity }} />
        </span>
      </motion.div>
    </section>
  );
}
